/**
 * Configuración de la aplicación
 * Detecta si corre en web o Android emulador para usar la URL correcta
 */

import { Capacitor } from '@capacitor/core';

type AppPlatform = 'web' | 'android' | 'ios';

function getPlatform(): AppPlatform {
    const platform = Capacitor.getPlatform();

    if (platform === 'android' || platform === 'ios') {
        return platform;
    }

    return 'web';
}

function resolvePlatformUrl(
    platform: AppPlatform,
    urls: {
        web: string;
        android: string;
        ios?: string;
    }
): string {
    if (platform === 'android') {
        return urls.android;
    }

    if (platform === 'ios') {
        return urls.ios?.trim() || urls.web;
    }

    return urls.web;
}

const appPlatform = getPlatform();

// Base URL de la API según la plataforma (desde variables de entorno)
export const API_BASE_URL = resolvePlatformUrl(appPlatform, {
    web: import.meta.env.VITE_API_URL_WEB,
    android: import.meta.env.VITE_API_URL_ANDROID,
    ios: import.meta.env.VITE_API_URL_IOS,
});

// URL de Reverb WebSocket (para uso futuro)
export const REVERB_URL = resolvePlatformUrl(appPlatform, {
    web: import.meta.env.VITE_REVERB_URL_WEB,
    android: import.meta.env.VITE_REVERB_URL_ANDROID,
    ios: import.meta.env.VITE_REVERB_URL_IOS,
});

// Reverb app key (requerida por Echo/Pusher protocol)
export const REVERB_APP_KEY = import.meta.env.VITE_REVERB_APP_KEY || '';

export const CARTA_PORTE_WIDGET_BASE_URL = (
    resolvePlatformUrl(appPlatform, {
        web: import.meta.env.VITE_CARTA_PORTE_WIDGET_URL_WEB ?? '',
        android: import.meta.env.VITE_CARTA_PORTE_WIDGET_URL_ANDROID ?? '',
        ios: import.meta.env.VITE_CARTA_PORTE_WIDGET_URL_IOS,
    })
) ?? '';

function normalizeCartaPorteWidgetBaseUrl(baseUrl: string): string {
    const trimmedUrl = baseUrl.trim().replace(/\/+$/, '');

    if (!trimmedUrl) {
        return '';
    }

    return trimmedUrl.endsWith('/app') ? trimmedUrl : `${trimmedUrl}/app`;
}

export function buildCartaPorteWidgetUrl(idCcp: string): string | null {
    const normalizedBaseUrl = normalizeCartaPorteWidgetBaseUrl(CARTA_PORTE_WIDGET_BASE_URL);

    if (!normalizedBaseUrl || !idCcp.trim()) {
        return null;
    }

    return `${normalizedBaseUrl}/${encodeURIComponent(idCcp)}?embed=true`;
}

export function getCartaPorteWidgetOrigin(): string | null {
    try {
        const normalizedBaseUrl = normalizeCartaPorteWidgetBaseUrl(CARTA_PORTE_WIDGET_BASE_URL);

        if (!normalizedBaseUrl) {
            return null;
        }

        return new URL(normalizedBaseUrl).origin;
    } catch {
        return null;
    }
}

// Endpoint de autenticación para canales privados
export const REVERB_AUTH_ENDPOINT = `${API_BASE_URL}/broadcasting/auth`;

// Clave para guardar el token en localStorage
export const TOKEN_STORAGE_KEY = 'auth_token';

// Tiempo de debounce para validaciones (ms)
export const VALIDATION_DEBOUNCE = 300;

// App version (Sprint 6)
export { APP_VERSION } from './version';
